import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';

import { BarStack, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';

import {
  getParams,
  getMonth,
  getMonths2,
  getMonths,
  thousandFormat,
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

// accessors
const x = (d) => parseInt(d.month);

const normalizeStatus = (status) => {
  const normalized = (status || '').toString().trim().toUpperCase();

  if (['OPEN', 'INPROGRESS', 'IN PROGRESS', 'ACTIVE', 'PIPELINE'].includes(normalized)) {
    return 'Open';
  }

  if (['CONVERTED', 'WON', 'WIN', 'COMPLETED'].includes(normalized)) {
    return 'Converted';
  }

  if (['LOST', 'DECLINED', 'CANCELLED', 'CANCELED'].includes(normalized)) {
    return 'Lost';
  }

  return (status || '').toString().trim();
};

let tooltipTimeout;

const TopChart = ({
  summaryData,
  selectedYears,
  selectedTopLeftLabels,
  selectedTopLeftItems,
  defaultStartMonth,
  defaultMonth,
  handleFilter,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [colors, setColors] = useState([]);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotal = {};
    let newData = [];
    let newTotals = [];

    let newColors = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    let fiscalMonths = getMonths(defaultStartMonth);
    let fiscalMonths2 = getMonths2(defaultMonth, defaultStartMonth);

    fiscalMonths.forEach((month) => {
      dictData[month] = {};
      dictData[month]['month'] = month;
      dictData[month]['Converted'] = 0;
      dictData[month]['Open'] = 0;
      dictData[month]['Lost'] = 0;
      dictTotal[month] = 0;
    });

    if (summaryData && summaryData['Left'])
      summaryData['Left'].forEach((item) => {
        let month = getMonth(item.Date);

        if (dictData[month] !== undefined) {
          if (fiscalMonths2.indexOf(month) > -1) {
            const status = normalizeStatus(item.Status);
            const hours = Number.parseFloat(item.Hours) || 0;

            if (dictData[month][status] !== undefined) {
              dictData[month][status] += hours;
            }
            dictTotal[month] += hours;
          }
        }
      });

    fiscalMonths.forEach((month) => {
      newData.push(dictData[month]);
      newTotals.push(dictTotal[month]);
    });

    setData(newData);
    setTotals(newTotals);
    setColors(newColors);
  }, [summaryData, selectedYears, defaultStartMonth, defaultMonth]);

  useEffect(() => {
    prepareData();
  }, [prepareData]);

  const getColor = useCallback((bar) => {
    const key = bar.key;
    const month = bar.bar.data.month;
    let activeColor = 0;
    let disableColor = 0;

    if (key === 'Open') {
      activeColor = '#919698';
      disableColor = '#e1e2e2';
    } else if (key === 'Converted') {
      activeColor = '#8dc63f';
      disableColor = '#e0ebd2';
    } else if (key === 'Lost') {
      activeColor = '#be1e2d';
      disableColor = '#e9cccf';
    } else {
      activeColor = '#e1e1e1';
      disableColor = '#e1e1e1';
    }

    if (selectedTopLeftItems.length === 0 && selectedTopLeftLabels.length === 0) {
      return activeColor;
    }
    if (selectedTopLeftLabels.indexOf(month) > -1) {
      return activeColor;
    }

    for (let i = 0; i < selectedTopLeftItems.length; i++) {
      if (selectedTopLeftItems[i].month === month) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedTopLeftLabels, selectedTopLeftItems]);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedTopLeftLabels, _selectedTopLeftItems;
    const month = bar.bar.data.month;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopLeftItems = selectedTopLeftItems.slice();
      _selectedTopLeftLabels = selectedTopLeftLabels.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedTopLeftItems.push({ month: month });
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedTopLeftItems.splice(index, 1);
      }
    } else {
      _selectedTopLeftLabels = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedTopLeftItems = [];
        _selectedBars = [];
      } else {
        _selectedTopLeftItems = [{ month: month }];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);
    handleFilter({
      selectedTopLeftItems: _selectedTopLeftItems,
      selectedTopLeftLabels: _selectedTopLeftLabels,
    });
  }, [selectedBars, selectedTopLeftItems, selectedTopLeftLabels, handleFilter]);

  const handleLabel = useCallback((event, month) => {
    let _selectedTopLeftLabels, _selectedTopLeftItems, _selectedBars;

    let index = NaN;
    for (let i = 0; i < selectedTopLeftLabels.length; i++) {
      if (selectedTopLeftLabels[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopLeftLabels = selectedTopLeftLabels.slice();
      _selectedTopLeftItems = selectedTopLeftItems.slice();

      if (isNaN(index)) {
        _selectedTopLeftLabels.push(month);
        _selectedTopLeftItems.push({ month: month });
      } else {
        _selectedTopLeftLabels.splice(index, 1);
        _selectedTopLeftItems.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedTopLeftItems = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      if (!isNaN(index) && selectedTopLeftLabels.length === 1) {
        _selectedTopLeftLabels = [];
        _selectedTopLeftItems = [];
      } else {
        _selectedTopLeftLabels = [month];
        _selectedTopLeftItems.push({ month: month });
      }
    }

    setSelectedBars(_selectedBars);
    handleFilter({
      selectedTopLeftLabels: _selectedTopLeftLabels,
      selectedTopLeftItems: _selectedTopLeftItems,
    });
  }, [selectedBars, selectedTopLeftLabels, selectedTopLeftItems, handleFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('oppSelectedTopLeftItems');
    localStorage.removeItem('oppSelectedTopLeftLabels');
    handleFilter({
      selectedTopLeftLabels: [],
      selectedTopLeftItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let tooltipData = {},
      top,
      left;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);

      top = event.clientY - 140;
      left = event.nativeEvent.layerX + 30;
      tooltipData = bar;
    } else {
      top = event.clientY - 140;
      left = event.nativeEvent.layerX + 30;
      tooltipData['forecast'] = bar;
    }

    tooltipData['isBar'] = isBar;

    showTooltip({
      tooltipData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  let xTransform = 30;
  let maxYValue = Math.max(
    Math.abs(Math.min(0, Math.min(...totals) * 1.3)),
    Math.max(...totals) * 1.3,
  );
  
  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (parseInt(thousandFormat(maxYValue)[0]) > 7)
      xTransform = 7.5 * (thousandFormat(maxYValue).length + 1) + 10;
    else xTransform = 7.5 * thousandFormat(maxYValue).length + 10;
  }

  xTransform = xTransform < 30 ? 30 : xTransform;

  const width = ((window.innerWidth - 60) * 7) / 12;
  const height = 200;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(window, width, height, margin);
  const offsetX = 3.5;
  const offsetWidth = -1.2;

  const xScale = scaleBand()
    .domain(data.map(x))
    .rangeRound([0, xMax])
    .padding(0.2);

  const yScale = scaleLinear({
    domain: [
      0,
      Math.max(...totals, 1) * 1.2,
    ],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  let fiscalMonths = getMonths(defaultStartMonth);
  let monthKeys = ['Lost', 'Converted', 'Open'];

  let line_data = [];
  line_data.push({ x: 0, y: isNaN(yScale(0)) ? 0 : yScale(0) });
  line_data.push({ x: xMax, y: isNaN(yScale(0)) ? 0 : yScale(0) });

  let posBase = [],
    negBase = [];
  data.forEach((i, key) => {
    posBase[key] = isNaN(yScale(0)) ? 0 : yScale(0);
    negBase[key] = isNaN(yScale(0)) ? 0 : yScale(0);
  });

  return (
    <div style={{ width: '100%' }}>
      <strong style={{ fontFamily: 'Verdana !important', fontSize: '12px' }}>
        Hours Spent
      </strong>
      <Grid container>
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
            onClick={deSelectAll}
          />

          <Group top={margin.top} left={margin.left}>
            <BarStack
              data={data}
              keys={monthKeys}
              x={x}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    let y0 = 0,
                      h0 = 0;
                    if (bar.height < 0) {
                      h0 = Math.abs(isNaN(bar.height) ? 0 : bar.height);
                      y0 = negBase[bar.index];
                      negBase[bar.index] -= isNaN(bar.height)
                        ? 0
                        : bar.height;
                    } else {
                      h0 = isNaN(bar.height) ? 0 : bar.height;
                      posBase[bar.index] -= isNaN(bar.height)
                        ? 0
                        : bar.height;
                      y0 = posBase[bar.index];
                    }

                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        x={bar.x}
                        y={y0}
                        height={h0}
                        width={bar.width}
                        fill={getColor(bar)}
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={hideTooltipHandler}
                        onMouseMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                        onTouchEnd={hideTooltipHandler}
                        onTouchMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                      />
                    );
                  });
                });
              }}
            </BarStack>

            <LinePath
              data={line_data}
              x={(nx) => nx.x}
              y={(nx) => nx.y}
              stroke="#555555"
              strokeDasharray="6,2"
            />

            <AxisLeft
              numTicks={5}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  {formattedValue}
                </text>
              )}
            />

            {fiscalMonths.map((month, index) => {
              return (
                <rect
                  key={index}
                  x={
                    (xScale.paddingInner() * xScale.step()) / 2 +
                    index * xScale.step() +
                    offsetX
                  }
                  y={yMax ? yMax : 0}
                  width={
                    (xMax - (xScale.paddingInner() * xScale.step()) / 2) /
                      fiscalMonths.length +
                    offsetWidth
                  }
                  height={margin.bottom}
                  fill="transparent"
                  onClick={(event) => handleLabel(event, month)}
                />
              );
            })}

            <AxisBottom
              hideTicks={true}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  style={{
                    fontWeight:
                      selectedTopLeftLabels.indexOf(parseInt(formattedValue)) > -1
                        ? 700
                        : 500,
                  }}
                  fill={
                    selectedTopLeftLabels.indexOf(parseInt(formattedValue)) > -1
                      ? activeLabelColor
                      : 'black'
                  }
                  onClick={(event) =>
                    handleLabel(event, parseInt(formattedValue))
                  }
                >
                  {enMonths[formattedValue]}
                </text>
              )}
            />
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <div className="bkg-white color-black">
                <div className="ft-12">
                  <strong>
                    Hours:{tooltipData.bar.data[tooltipData.key]}
                  </strong>
                </div>
              </div>
            ) : null}
          </TooltipWithBounds>
        )}
      </Grid>

      <div style={{ marginTop: 10 }}>
        <svg height={20} width={xMax}>
          <Group top={0} left={0}>
            <rect x={0} y={0} width={12} height={12} fill="#8dc63f" />
            <text x={14} y={12} fontSize={12}>
              Converted
            </text>
          </Group>
          <Group top={0} left={0}>
            <rect x={100} y={0} width={12} height={12} fill="#919698" />
            <text x={114} y={12} fontSize={12}>
              Open
            </text>
          </Group>
          <Group top={0} left={0}>
            <rect x={200} y={0} width={12} height={12} fill="#be1e2d" />
            <text x={214} y={12} fontSize={12}>
              Lost
            </text>
          </Group>
        </svg>
      </div>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.object.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedTopLeftLabels: PropTypes.array.isRequired,
  selectedTopLeftItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
